package org.example.project.engineslot

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.example.project.service.InfrastrukturApi
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.AppDropdownMenuMode
import org.example.project.settings.LumaticaButton
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

const val SLOT_EXTRA_DRIVER_ID = "slotDriverUserId"
const val SLOT_EXTRA_LIEFERN_BELEGE = "slotLiefernBelege"
const val SLOT_EXTRA_LIEFERANT_FIRMEN_ID = "slotLieferantFirmenId"
const val SLOT_EXTRA_LIEFERN_EMPFAENGER_FIRMEN_ID = "slotEmpfaengerFirmenId"
private const val SLOT_EXTRA_LIEFERN_AUFTRAGGEBER_FIRMEN_ID = "slotAuftraggeberFirmenId"
const val SLOT_EXTRA_UNTERNEHMER_FIRMEN_ID = "slotUnternehmerFirmenId"
private const val SLOT_EXTRA_LHM_TYP_ID = "slotLhmTypId"
private const val SLOT_EXTRA_LHM_TYP = "slotLhmTyp"
private const val SLOT_SIMPLE_CARD_WIDTH_PX = 760
private const val SLOT_INLINE_LABEL_WIDTH_PX = 176

val slotLiefernJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

private fun slotLiefernCompactBeleg(beleg: All): All =
    All(
        id = beleg.id,
        lieferscheinnummer = beleg.lieferscheinnummer,
        palettenanzahl = beleg.palettenanzahl.coerceAtLeast(0),
        fahrername = beleg.fahrername,
        lkwkennzeichen = beleg.lkwkennzeichen,
        aufliegerkennzeichen = beleg.aufliegerkennzeichen,
        lieferant = beleg.lieferant,
        auftraggeber = beleg.auftraggeber,
        empfaenger = beleg.empfaenger,
        unternehmer = beleg.unternehmer,
        zustand = beleg.zustand,
        slotDriverUserId = beleg.slotDriverUserId,
        lieferantFirmenId = beleg.lieferantFirmenId,
        empfaengerFirmenId = beleg.empfaengerFirmenId,
        auftraggeberFirmenId = beleg.auftraggeberFirmenId,
        unternehmerFirmenId = beleg.unternehmerFirmenId,
        slotLhmTypId = beleg.slotLhmTypId,
        slotLhmTyp = beleg.slotLhmTyp,
        lhmTypId = beleg.lhmTypId,
        typ = beleg.typ,
        endToEndFlow = beleg.endToEndFlow,
        flowWeAvis = beleg.flowWeAvis,
        flowTyp = beleg.flowTyp,
        flowStatus = beleg.flowStatus,
        flowId = beleg.flowId,
        flowSourceFirmenId = beleg.flowSourceFirmenId,
        flowSourceWaTour = beleg.flowSourceWaTour,
        flowSourceWaLkwId = beleg.flowSourceWaLkwId,
        flowTargetFirmenId = beleg.flowTargetFirmenId,
        flowTargetWeId = beleg.flowTargetWeId,
        flowTargetWeLieferscheinnummer = beleg.flowTargetWeLieferscheinnummer
    )

@Composable
fun SlotBookingLiefernContent(
    state: All,
    masterData: List<All>,
    standorte: List<All>,
    zugmaschinen: List<All>,
    auflieger: List<All>,
    hasMultipleStandorte: Boolean,
    selectedAbholort: String,
    selectedAbholortLabel: String,
    readOnly: Boolean = false,
    onStateChange: (All) -> Unit
) {
    var users by remember { mutableStateOf<List<All>>(emptyList()) }
    var roles by remember { mutableStateOf<List<All>>(emptyList()) }
    var lhmTypen by remember { mutableStateOf<List<All>>(emptyList()) }
    var slotBookings by remember { mutableStateOf<List<All>>(emptyList()) }
    var driverSchedulePopup by remember { mutableStateOf<List<String>?>(null) }

    LaunchedEffect(Unit) {
        users = loadSlotCompanyUsers()
        roles = loadSlotVisibleRoles()
        lhmTypen = InfrastrukturApi.loadLhmTypen()
    }

    LaunchedEffect(state.firmenId, state.datum) {
        slotBookings = runCatching { loadSlotDriverScheduleBookings(state.firmenId) }.getOrDefault(emptyList())
    }

    val driverRoleIds = remember(roles) {
        roles.filter { role ->
            role.name.ifBlank { role.bezeichnung }.contains("fahrer", ignoreCase = true) ||
                role.id.equals("fahrer", ignoreCase = true)
        }.map { it.id }.toSet()
    }
    val driverUsers = remember(users, driverRoleIds) {
        users.filter { user ->
            val rolleId = user.rolle.trim()
            rolleId.isNotBlank() && (rolleId in driverRoleIds || rolleId.equals("fahrer", ignoreCase = true))
        }.sortedBy {
            listOf(it.vorname, it.nachname).filter { part -> part.isNotBlank() }.joinToString(" ").lowercase()
        }
    }
    val driverOptions = remember(driverUsers, slotBookings, state.datum) {
        listOf(All(key = "", label = "Keine Auswahl")) + driverUsers.map { user ->
            val name = slotDriverDisplayName(user)
            val count = slotDriverDayScheduleEntries(slotBookings, user.id, state.datum).size
            All(
                key = user.id,
                label = slotDriverOptionLabel(name, count)
            )
        }
    }
    val lieferantOptions = remember(masterData) { slotAddressDropdownOptions(masterData, SLOT_MASTER_EMPFAENGER) }
    val empfaengerOptions = remember(masterData) { slotAddressDropdownOptions(masterData, SLOT_MASTER_EMPFAENGER) }
    val auftraggeberOptions = remember(masterData) { slotAddressDropdownOptions(masterData, SLOT_MASTER_AUFTRAGGEBER) }
    val unternehmerOptions = remember(masterData) { slotAddressDropdownOptions(masterData, SLOT_MASTER_LADEFIRMA) }
    val lhmOptions = remember(lhmTypen) {
        listOf(All(key = "", label = "Keine Auswahl")) + lhmTypen
            .sortedBy { it.id.toIntOrNull() ?: 0 }
            .map { All(key = it.id, label = it.bezeichnung.ifBlank { it.kurzbezeichnung.ifBlank { it.id } }) }
    }
    val defaultLhm = remember(lhmOptions) {
        lhmOptions.filter { it.key.isNotBlank() }.singleOrNull()
    }
    val defaultEmpfaenger = remember(empfaengerOptions, state.firmenId) {
        slotDefaultAddressOption(empfaengerOptions, state.firmenId)
    }
    val belege = remember(state, defaultEmpfaenger) {
        slotLiefernBelege(state).map { beleg ->
            if (beleg.empfaenger.isNotBlank() || defaultEmpfaenger == null) {
                beleg
            } else {
                beleg.copy(
                    empfaenger = defaultEmpfaenger.label,
                    empfaengerFirmenId = defaultEmpfaenger.key
                )
            }
        }
    }
    val endToEndTargetWe = state.endToEndFlow && state.flowTyp == "wa_to_we"

    fun pushBelege(updatedBelege: List<All>) {
        val sharedDriverId = updatedBelege.firstNotNullOfOrNull { it.slotDriverUserId.takeIf(String::isNotBlank) }.orEmpty()
        val sharedFahrer = updatedBelege.firstNotNullOfOrNull { it.fahrername.takeIf(String::isNotBlank) }.orEmpty()
        val sharedLkw = updatedBelege.firstNotNullOfOrNull { it.lkwkennzeichen.takeIf(String::isNotBlank) }.orEmpty()
        val sharedAuflieger = updatedBelege.firstNotNullOfOrNull { it.aufliegerkennzeichen.takeIf(String::isNotBlank) }.orEmpty()
        val firstEmpfaengerId = updatedBelege.firstNotNullOfOrNull { it.empfaengerFirmenId.takeIf(String::isNotBlank) }.orEmpty()
        val normalized = updatedBelege
            .ifEmpty { listOf(slotLiefernDefaultBeleg(state)) }
            .map { beleg ->
                if (updatedBelege.size <= 1) beleg else beleg.copy(
                    fahrername = sharedFahrer,
                    lkwkennzeichen = sharedLkw,
                    aufliegerkennzeichen = sharedAuflieger,
                    slotDriverUserId = sharedDriverId
                )
            }
            .map(::slotLiefernCompactBeleg)
        val first = normalized.first()
        onStateChange(
            state.copy(
                lieferscheinnummer = first.lieferscheinnummer,
                palettenanzahl = normalized.sumOf { it.palettenanzahl.coerceAtLeast(0) },
                fahrername = first.fahrername,
                lkwkennzeichen = first.lkwkennzeichen,
                aufliegerkennzeichen = first.aufliegerkennzeichen,
                lieferant = first.lieferant,
                auftraggeber = first.auftraggeber,
                empfaenger = first.empfaenger,
                unternehmer = first.unternehmer,
                slotLiefernBelege = slotLiefernJson.encodeToString(
                        ListSerializer(All.AllNames.serializer()),
                        normalized
                    ),
                slotDriverUserId = first.slotDriverUserId,
                lieferantFirmenId = first.lieferantFirmenId,
                empfaengerFirmenId = first.empfaengerFirmenId.ifBlank { firstEmpfaengerId },
                auftraggeberFirmenId = first.auftraggeberFirmenId,
                unternehmerFirmenId = first.unternehmerFirmenId
            )
        )
    }

    LaunchedEffect(defaultEmpfaenger?.key, state.id, state.empfaenger) {
        if (defaultEmpfaenger != null && state.empfaenger.isBlank()) {
            pushBelege(belege)
        }
    }

    LaunchedEffect(defaultLhm?.key, state.id) {
        val default = defaultLhm ?: return@LaunchedEffect
        if (belege.any { it.lhmTypId.isBlank() }) {
            pushBelege(
                belege.map { beleg ->
                    if (beleg.lhmTypId.isBlank()) {
                        beleg.copy(
                            slotLhmTypId = default.key,
                            slotLhmTyp = default.label,
                            lhmTypId = default.key,
                            typ = default.label
                        )
                    } else {
                        beleg
                    }
                }
            )
        }
    }

    val belegSummary = belege.joinToString("  |  ") { beleg ->
        val nummer = beleg.lieferscheinnummer.ifBlank { "-" }
        "$nummer (${beleg.palettenanzahl.coerceAtLeast(0)})"
    }

    Div({
        style {
            width(100.percent)
            height(100.percent)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(18.px)
            padding(0.px, 0.px, 30.px, 0.px)
            boxSizing("border-box")
            overflow("hidden")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                minHeight(44.px)
                marginBottom(30.px)
            }
        }) {
            Span({
                style {
                    color(Color.white)
                    fontSize(18.px)
                    fontWeight("700")
                    textAlign("center")
                }
            }) { Text(belegSummary) }
        }

        Div({
            style {
                flex(1)
                overflowX("auto")
                overflowY("auto")
                padding(0.px, 0.px, 24.px, 0.px)
            }
        }) {
            if (endToEndTargetWe) {
                Div({
                    style {
                        color(Color.white)
                        fontSize(15.px)
                        fontWeight("700")
                        textAlign("center")
                        padding(12.px, 18.px)
                        backgroundColor(Color("rgba(255,255,255,0.055)"))
                        property("border", "1px solid rgba(255,255,255,0.12)")
                        borderRadius(10.px)
                    }
                }) {
                    Text("Deine Lieferadresse wurde übernommen. Die Palettenanzahl wird nach dem WA-Ausgang gesetzt.")
                }
            }

            Div({
                style {
                    minWidth(100.percent)
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    paddingBottom(18.px)
                    boxSizing("border-box")
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.FlexStart)
                        gap(32.px)
                        property("width", "max-content")
                    }
                }) {
                    belege.forEachIndexed { belegIndex, beleg ->
                        val selectedDriverId = beleg.slotDriverUserId
                        val lkwOptions = remember(zugmaschinen, beleg.lkwkennzeichen) {
                            slotVehicleDropdownOptions(zugmaschinen, beleg.lkwkennzeichen)
                        }
                        val aufliegerOptions = remember(auflieger, beleg.aufliegerkennzeichen) {
                            slotVehicleDropdownOptions(auflieger, beleg.aufliegerkennzeichen, showAufliegerMerkmale = true)
                        }

                        Div({
                            style {
                                width(SLOT_SIMPLE_CARD_WIDTH_PX.px)
                                minHeight(360.px)
                                display(DisplayStyle.Flex)
                                flexDirection(FlexDirection.Column)
                                gap(18.px)
                                padding(24.px, 28.px, 28.px, 28.px)
                                boxSizing("border-box")
                                backgroundColor(Color("rgba(255,255,255,0.025)"))
                                property("border", "1px solid rgba(255,255,255,0.08)")
                                borderRadius(16.px)
                                property("box-shadow", "0 10px 24px rgba(0,0,0,0.12)")
                                flexShrink(0)
                                position(Position.Relative)
                            }
                        }) {
                            if (!readOnly && belege.size > 1) {
                                Div({
                                    style {
                                        position(Position.Absolute)
                                        top(10.px)
                                        right(10.px)
                                    }
                                }) {
                                    LumaticaButton(
                                        label = "",
                                        onClick = { pushBelege(belege.filterIndexed { index, _ -> index != belegIndex }) },
                                        heightPx = 26,
                                        paddingH = 6
                                    ) {
                                        Img(src = "images/miniclose.svg") {
                                            style {
                                                width(12.px)
                                                height(12.px)
                                            }
                                        }
                                    }
                                }
                            }
                            Span({
                                style {
                                    color(Color.white)
                                    fontSize(16.px)
                                    fontWeight("700")
                                    textAlign("center")
                                    width(100.percent)
                                }
                            }) { Text("Lieferposition ${belegIndex + 1}") }

                            if (readOnly) {
                                SlotInlineReadonlyRow("lieferscheinnummer", beleg.lieferscheinnummer)
                                SlotInlineReadonlyRow(
                                    "Palettenanzahl",
                                    beleg.palettenanzahl.toString().takeIf { beleg.palettenanzahl > 0 }.orEmpty()
                                )
                                SlotInlineReadonlyRow("LHM-Typ", beleg.typ)
                                SlotInlineReadonlyRow("Lieferant", beleg.lieferant)
                                SlotInlineReadonlyRow("Empfänger", beleg.empfaenger)
                                SlotInlineReadonlyRow("Auftraggeber", beleg.auftraggeber)
                                SlotInlineReadonlyRow("Unternehmer", beleg.unternehmer)
                                SlotInlineReadonlyRow("Fahrer", beleg.fahrername)
                                SlotInlineReadonlyRow("LKW", beleg.lkwkennzeichen)
                                SlotInlineReadonlyRow("Auflieger", beleg.aufliegerkennzeichen)
                            } else {
                                SlotInlineInputRow("lieferscheinnummer", beleg.lieferscheinnummer) { value ->
                                    pushBelege(belege.updatedAt(belegIndex, beleg.copy(lieferscheinnummer = value)))
                                }
                                if (endToEndTargetWe) {
                                    SlotInlineReadonlyRow("Palettenanzahl", "wird nach WA-Ausgang gesetzt")
                                } else {
                                    SlotInlineInputRow(
                                        "Palettenanzahl",
                                        beleg.palettenanzahl.toString().takeIf { beleg.palettenanzahl > 0 }.orEmpty()
                                    ) { value ->
                                        pushBelege(
                                            belege.updatedAt(
                                                belegIndex,
                                                beleg.copy(palettenanzahl = value.toIntOrNull() ?: 0)
                                            )
                                        )
                                    }
                                }
                                SlotInlineDropdownRow("LHM-Typ", lhmOptions, beleg.lhmTypId) { value ->
                                    val selected = lhmOptions.firstOrNull { it.key == value }
                                    pushBelege(
                                        belege.updatedAt(
                                            belegIndex,
                                            beleg.copy(
                                                slotLhmTypId = value,
                                                slotLhmTyp = selected?.label.orEmpty(),
                                                lhmTypId = value,
                                                typ = selected?.label.orEmpty()
                                            )
                                        )
                                    )
                                }
                                SlotInlineDropdownRow(
                                    "Lieferant",
                                    lieferantOptions,
                                    slotAddressSelectedKey(lieferantOptions, beleg.lieferantFirmenId, beleg.lieferant)
                                ) { value ->
                                    val selected = lieferantOptions.firstOrNull { it.key == value }
                                    pushBelege(
                                        belege.updatedAt(
                                            belegIndex,
                                            beleg.copy(
                                                lieferant = selected?.label.orEmpty(),
                                                lieferantFirmenId = selected?.firmenId.orEmpty()
                                            )
                                        )
                                    )
                                }
                                SlotInlineDropdownRow(
                                    "Empfänger",
                                    empfaengerOptions,
                                    slotAddressSelectedKey(empfaengerOptions, beleg.empfaengerFirmenId, beleg.empfaenger)
                                ) { value ->
                                    val selected = empfaengerOptions.firstOrNull { it.key == value }
                                    pushBelege(
                                        belege.updatedAt(
                                            belegIndex,
                                            beleg.copy(
                                                empfaenger = selected?.label.orEmpty(),
                                                empfaengerFirmenId = selected?.firmenId.orEmpty()
                                            )
                                        )
                                    )
                                }
                                SlotInlineDropdownRow(
                                    "Auftraggeber",
                                    auftraggeberOptions,
                                    slotAddressSelectedKey(auftraggeberOptions, beleg.auftraggeberFirmenId, beleg.auftraggeber)
                                ) { value ->
                                    val selected = auftraggeberOptions.firstOrNull { it.key == value }
                                    pushBelege(
                                        belege.updatedAt(
                                            belegIndex,
                                            beleg.copy(
                                                auftraggeber = selected?.label.orEmpty(),
                                                auftraggeberFirmenId = selected?.firmenId.orEmpty()
                                            )
                                        )
                                    )
                                }
                                SlotInlineDropdownRow(
                                    "Unternehmer",
                                    unternehmerOptions,
                                    slotAddressSelectedKey(unternehmerOptions, beleg.unternehmerFirmenId, beleg.unternehmer)
                                ) { value ->
                                    val selected = unternehmerOptions.firstOrNull { it.key == value }
                                    pushBelege(
                                        belege.updatedAt(
                                            belegIndex,
                                            beleg.copy(
                                                unternehmer = selected?.label.orEmpty(),
                                                unternehmerFirmenId = selected?.firmenId.orEmpty()
                                            )
                                        )
                                    )
                                }
                                SlotInlineDropdownRow("Fahrer", driverOptions, selectedDriverId) { driverId ->
                                    val driver = driverUsers.firstOrNull { it.id == driverId }
                                    pushBelege(
                                        belege.updatedAt(
                                            belegIndex,
                                            beleg.copy(
                                                fahrername = driver?.let(::slotDriverDisplayName).orEmpty(),
                                                lkwkennzeichen = driver?.lkwkennzeichen.orEmpty(),
                                                aufliegerkennzeichen = driver?.aufliegerkennzeichen.orEmpty(),
                                                slotDriverUserId = driverId
                                            )
                                        )
                                    )
                                    val schedule = slotDriverDayScheduleEntries(slotBookings, driverId, state.datum)
                                    if (schedule.isNotEmpty()) driverSchedulePopup = schedule
                                }
                                SlotInlineDropdownRow("LKW", lkwOptions, beleg.lkwkennzeichen) { value ->
                                    pushBelege(belege.updatedAt(belegIndex, beleg.copy(lkwkennzeichen = value)))
                                }
                                SlotInlineDropdownRow("Auflieger", aufliegerOptions, beleg.aufliegerkennzeichen) { value ->
                                    pushBelege(belege.updatedAt(belegIndex, beleg.copy(aufliegerkennzeichen = value)))
            }
        }
    }

    driverSchedulePopup?.let { entries ->
        SlotDriverSchedulePopup(
            entries = entries,
            onClose = { driverSchedulePopup = null }
        )
    }
}
                }
            }
        }
    }

}

@Composable
private fun SlotInlineInputRow(
    label: String,
    value: String,
    onChange: (String) -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(16.px)
            minWidth(0.px)
        }
    }) {
        SlotInlineLabel(label)
        Input(InputType.Text, attrs = {
            value(value)
            style {
                flex(1)
                minWidth(0.px)
                height(38.px)
                padding(0.px, 0.px, 6.px, 0.px)
                backgroundColor(Color.transparent)
                color(Color.white)
                fontSize(16.px)
                border(0.px)
                property("border-bottom", "1px solid rgba(255,255,255,0.24)")
                property("outline", "none")
                boxSizing("border-box")
            }
            onInput { onChange(it.value) }
        })
    }
}

@Composable
private fun SlotInlineReadonlyRow(
    label: String,
    value: String
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.FlexStart)
            gap(16.px)
            minWidth(0.px)
        }
    }) {
        SlotInlineLabel(label)
        Div({
            style {
                flex(1)
                minHeight(38.px)
                padding(0.px, 0.px, 6.px, 0.px)
                property("border-bottom", "1px solid rgba(255,255,255,0.24)")
                color(Color.white)
                fontSize(16.px)
                lineHeight("1.35")
                property("white-space", "normal")
                property("overflow-wrap", "anywhere")
                boxSizing("border-box")
            }
        }) {
            Text(value.ifBlank { "-" })
        }
    }
}

@Composable
private fun SlotInlineDropdownRow(
    label: String,
    options: List<All>,
    selectedValue: String,
    onChange: (String) -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(16.px)
            minWidth(0.px)
        }
    }) {
        SlotInlineLabel(label)
        AppDropdown(
            options = options,
            selectedValue = selectedValue.ifBlank { null },
            onChange = onChange,
            placeholder = "Auswahl",
            appearance = AppDropdownAppearance.Underline,
            menuMode = AppDropdownMenuMode.Overlay,
            attrs = {
                style {
                    flex(1)
                    minWidth(0.px)
                    height(38.px)
                    padding(0.px, 0.px, 6.px, 0.px)
                    fontSize(16.px)
                }
            }
        )
    }
}

@Composable
private fun SlotInlineLabel(label: String) {
    Span({
        style {
            width(SLOT_INLINE_LABEL_WIDTH_PX.px)
            property("min-width", "${SLOT_INLINE_LABEL_WIDTH_PX}px")
            color(Color("#8a8f9a"))
            fontSize(14.px)
            fontWeight("700")
            letterSpacing(0.2.px)
            lineHeight("1.3")
        }
    }) { Text(label) }
}

fun slotLiefernBelege(state: All): List<All> {
    val stored = state.slotLiefernBelege
    val parsed = runCatching {
        slotLiefernJson.decodeFromString(ListSerializer(All.AllNames.serializer()), stored)
    }.getOrDefault(emptyList())
    if (parsed.isNotEmpty()) return parsed.map(::slotLiefernCompactBeleg)
    return listOf(slotLiefernDefaultBeleg(state))
}

fun slotLiefernDefaultBeleg(
    state: All,
    index: Int = 1,
    prefillFromState: Boolean = true
): All =
    All(
        id = "beleg-$index",
        lieferscheinnummer = if (prefillFromState) state.lieferscheinnummer else "",
        palettenanzahl = if (prefillFromState) state.palettenanzahl else 0,
        fahrername = if (prefillFromState) state.fahrername else "",
        lkwkennzeichen = if (prefillFromState) state.lkwkennzeichen else "",
        aufliegerkennzeichen = if (prefillFromState) state.aufliegerkennzeichen else "",
        lieferant = if (prefillFromState) state.lieferant else "",
        auftraggeber = if (prefillFromState) state.auftraggeber else "",
        empfaenger = if (prefillFromState) state.empfaenger else "",
        unternehmer = if (prefillFromState) state.unternehmer else "",
        zustand = if (prefillFromState) state.zustand else 0,
        slotDriverUserId = if (prefillFromState) state.slotDriverUserId else "",
        lieferantFirmenId = if (prefillFromState) state.lieferantFirmenId else "",
        empfaengerFirmenId = if (prefillFromState) state.empfaengerFirmenId else "",
        auftraggeberFirmenId = if (prefillFromState) state.auftraggeberFirmenId else "",
        unternehmerFirmenId = if (prefillFromState) state.unternehmerFirmenId else "",
        slotLhmTypId = if (prefillFromState) state.slotLhmTypId else "",
        slotLhmTyp = if (prefillFromState) state.slotLhmTyp else "",
        lhmTypId = if (prefillFromState) state.lhmTypId else "",
        typ = if (prefillFromState) state.typ else "",
        endToEndFlow = if (prefillFromState) state.endToEndFlow else false,
        flowWeAvis = if (prefillFromState) state.flowWeAvis else false,
        flowTyp = if (prefillFromState) state.flowTyp else "",
        flowStatus = if (prefillFromState) state.flowStatus else "",
        flowId = if (prefillFromState) state.flowId else "",
        flowSourceFirmenId = if (prefillFromState) state.flowSourceFirmenId else "",
        flowSourceWaTour = if (prefillFromState) state.flowSourceWaTour else "",
        flowSourceWaLkwId = if (prefillFromState) state.flowSourceWaLkwId else "",
        flowTargetFirmenId = if (prefillFromState) state.flowTargetFirmenId else "",
        flowTargetWeId = if (prefillFromState) state.flowTargetWeId else "",
        flowTargetWeLieferscheinnummer = if (prefillFromState) state.flowTargetWeLieferscheinnummer else ""
    )

fun slotLiefernAppendBeleg(state: All): All {
    val belege = slotLiefernBelege(state)
    val nextBelege = belege + slotLiefernDefaultBeleg(
        state = state,
        index = belege.size + 1,
        prefillFromState = false
    ).copy(
        fahrername = belege.firstOrNull()?.fahrername.orEmpty(),
        lkwkennzeichen = belege.firstOrNull()?.lkwkennzeichen.orEmpty(),
        aufliegerkennzeichen = belege.firstOrNull()?.aufliegerkennzeichen.orEmpty(),
        slotDriverUserId = belege.firstOrNull()?.slotDriverUserId.orEmpty(),
        lieferantFirmenId = "",
        empfaengerFirmenId = "",
        auftraggeberFirmenId = "",
        unternehmerFirmenId = "",
        slotLhmTypId = belege.firstOrNull()?.slotLhmTypId.orEmpty(),
        slotLhmTyp = belege.firstOrNull()?.slotLhmTyp.orEmpty(),
        lhmTypId = belege.firstOrNull()?.lhmTypId.orEmpty(),
        typ = belege.firstOrNull()?.typ.orEmpty()
    )
    val normalized = nextBelege.map(::slotLiefernCompactBeleg)
    val first = normalized.first()
    return state.copy(
        lieferscheinnummer = first.lieferscheinnummer,
        palettenanzahl = normalized.sumOf { it.palettenanzahl.coerceAtLeast(0) },
        fahrername = first.fahrername,
        lkwkennzeichen = first.lkwkennzeichen,
        aufliegerkennzeichen = first.aufliegerkennzeichen,
        lieferant = first.lieferant,
        auftraggeber = first.auftraggeber,
        empfaenger = first.empfaenger,
        unternehmer = first.unternehmer,
        slotLiefernBelege = slotLiefernJson.encodeToString(
                ListSerializer(All.AllNames.serializer()),
                normalized
            ),
        slotDriverUserId = first.slotDriverUserId,
        lieferantFirmenId = first.lieferantFirmenId,
        empfaengerFirmenId = first.empfaengerFirmenId,
        auftraggeberFirmenId = first.auftraggeberFirmenId,
        unternehmerFirmenId = first.unternehmerFirmenId,
        slotLhmTypId = first.slotLhmTypId,
        slotLhmTyp = first.slotLhmTyp,
        lhmTypId = first.lhmTypId,
        typ = first.typ
    )
}

private fun List<All>.updatedAt(index: Int, value: All): List<All> =
    mapIndexed { currentIndex, current -> if (currentIndex == index) value else current }

private fun slotAddressDropdownOptions(entries: List<All>, typ: String): List<All> =
    listOf(All(key = "", label = "Keine Auswahl")) +
        slotMasterEntries(entries, typ).map { entry ->
            val simpleLabel = listOf(
                entry.firma.ifBlank { entry.name },
                listOf(entry.strasse, entry.hausnummer).filter { it.isNotBlank() }.joinToString(" "),
                listOf(entry.plz, entry.ort).filter { it.isNotBlank() }.joinToString(" ")
            ).filter { it.isNotBlank() }.joinToString(", ").ifBlank { entry.id }
            All(
                key = entry.firmenId.ifBlank { "local:${entry.id.ifBlank { simpleLabel }}" },
                label = simpleLabel,
                firmenId = entry.firmenId
            )
        }.distinctBy { it.label.lowercase() }

private fun slotDefaultAddressOption(options: List<All>, slotOwnerFirmenId: String): All? =
    options.firstOrNull {
        it.key.isNotBlank() &&
            it.key == slotOwnerFirmenId &&
            it.label.isNotBlank()
    }

private fun slotAddressSelectedKey(options: List<All>, storedKey: String, label: String): String =
    storedKey.ifBlank {
        options.firstOrNull { it.label == label }?.key.orEmpty()
    }

